import { InfoCircleOutlined } from "@ant-design/icons";
import { Card, Col, Row, Statistic, Tooltip } from "antd";
import formatarDuracao from "../utils/formatarDuracao";

const formatCurrency = (value) => {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const formatNumberBr = (value) => {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getPrecisionBySuffix = (suffix = "") => {
  if (suffix.includes("%")) {
    return 1;
  }
  if (suffix.includes("R$/min")) {
    return 2;
  }
  return 0;
};

const formatDefaultNumberBr = (value, precision = 0) => {
  const numericValue = Number(value || 0);
  const resolvedPrecision =
    precision > 0 ? precision : Number.isInteger(numericValue) ? 0 : 2;

  return numericValue.toLocaleString("pt-BR", {
    minimumFractionDigits: resolvedPrecision,
    maximumFractionDigits: resolvedPrecision,
  });
};

const formatVariation = (value, unit) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const sinal = value > 0 ? "+" : "";
  const sufixo = unit === "pp" ? " p.p." : "%";
  return `${sinal}${value.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}${sufixo}`;
};

const MetricStatistic = ({ kpi, data, unavailableKeys }) => {
  const isUnavailable = unavailableKeys.includes(kpi.key);
  const variacao = data?.[kpi.variationKey];
  const textoDaVariacao = formatVariation(variacao, kpi.variationUnit);
  const corDaVariacao =
    variacao > 0 ? "#389e0d" : variacao < 0 ? "#cf1322" : undefined;
  const formatador =
    kpi.formatter === "currency"
      ? formatCurrency
      : kpi.formatter === "number_br"
        ? formatNumberBr
        : kpi.formatter === "duration"
          ? formatarDuracao
          : (value) =>
              formatDefaultNumberBr(
                value,
                getPrecisionBySuffix(kpi.suffix || "")
              );

  return (
    <Statistic
      style={{ minWidth: 130 }}
      title={
        <>
          {kpi.title}
          <Tooltip title={kpi.description}>
            <InfoCircleOutlined style={{ marginLeft: 10 }} />
          </Tooltip>
        </>
      }
      value={
        isUnavailable
          ? "—"
          : kpi.key.includes("percent")
            ? (data?.[kpi.key] || 0) * 100
            : data?.[kpi.key] || 0
      }
      formatter={
        isUnavailable
          ? (value) => value
          : (value) => (
              <>
                {formatador(value)}
                {kpi.suffix}
                {textoDaVariacao && (
                  <span
                    style={{
                      color: corDaVariacao,
                      fontSize: "0.62em",
                      marginLeft: 6,
                    }}>
                    ({textoDaVariacao})
                  </span>
                )}
              </>
            )
      }
    />
  );
};

// Chaves listadas em unavailableKeys renderizam "—" em vez de um número. Serve
// para o caso em que a métrica não existe, que é diferente de valer zero: uma
// loja sem zona cadastrada não tem 0 % de ocupação, ela não tem ocupação.
export default function MainMetrics({
  data,
  isLoading,
  kpis,
  unavailableKeys = [],
}) {
  return (
    <Card
      loading={isLoading}
      title={"Principais métricas"}
      align="center"
      style={{
        width: "85%",
        maxWidth: "100%",
      }}
      styles={{
        header: {
          backgroundColor: "var(--ouran-card-header-bg)",
        },
      }}>
      <Row
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "32px",
          maxWidth: "100%",
        }}>
        {kpis?.map((kpi) => {
          const metricas = kpi.metrics || [kpi];

          return (
            <Col
              key={kpi.key}
              className="ouran-main-metrics-col"
              style={{
                flex: "1 1 calc(20%)",
                minWidth: "200px",
              }}>
              <Card
                bordered={true}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  }}>
                  {metricas.map((metrica, indice) => (
                    <div
                      key={metrica.key}
                      style={{
                        flex: 1,
                        paddingLeft: indice ? 16 : 0,
                        marginLeft: indice ? 16 : 0,
                        borderLeft: indice ? "1px solid #f0f0f0" : undefined,
                      }}>
                      <MetricStatistic
                        kpi={metrica}
                        data={data}
                        unavailableKeys={unavailableKeys}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}
